import React, { useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { v4API } from '../../Api';

export const SECRET_PURPOSES = { credentialRef: 'processing', webhookSecretRef: 'webhook', callbackSecretRef: 'callback', apiKeyRef: 'legacy_callback' };
export const secretMetadata = item => ({ ref: item.ref, label: item.label, purpose: item.purpose, configured: item.configured, source: item.source, createdAt: item.createdAt });
export const secretSource = source => source === 'database' ? 'Зашифровано в БД' : 'Окружение сервера';

const PROVIDER_FIELDS = {
  platformapi: [{ key: 'apiKey', label: 'API-ключ Merchant API' }],
  bestmerchant: [{ key: 'apiKey', label: 'API-ключ' }],
  cardbtc: [{ key: 'apiKey', label: 'API-ключ' }, { key: 'btcWallet', label: 'BTC-кошелёк для выплат' }],
  nicepay: [{ key: 'merchantId', label: 'ID мерчанта провайдера' }, { key: 'secret', label: 'Секрет' }],
  payfast: [{ key: 'apiKey', label: 'API-ключ' }, { key: 'secretKey', label: 'Секретный ключ подписи' }],
  '911pay': [{ key: 'accessToken', label: 'Access Token' }, { key: 'merchantId', label: 'ID мерчанта провайдера' }],
  primepay: [{ key: 'apiKey', label: 'API-ключ' }, { key: 'apiSecret', label: 'Секрет API' }],
  xpay: [{ key: 'apiKey', label: 'API-ключ' }, { key: 'merchantId', label: 'ID мерчанта провайдера' }],
  xpaysng: [{ key: 'apiKey', label: 'API-ключ' }, { key: 'merchantId', label: 'ID мерчанта провайдера' }],
  xpaysim: [{ key: 'apiKey', label: 'API-ключ' }, { key: 'merchantId', label: 'ID мерчанта провайдера' }],
};

function fieldsFor(purpose, provider) {
  if (purpose !== 'processing') return [{ key: 'value', label: 'Значение секрета' }];
  if (provider === 'payscrow') return [{ key: 'value', label: 'API-ключ' }];
  if (provider === 'savagepay') return [{ key: 'value', label: 'API-токен SavagePay' }];
  return PROVIDER_FIELDS[provider] || [{ key: 'json', label: 'Учётные данные (JSON)' }];
}

export default function SecretEditor({ purpose, provider, title = 'Добавить секрет', onCreated, onClose }) {
  const [label, setLabel] = useState('');
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fields = fieldsFor(purpose, provider);
  const close = () => { setValues({}); setLabel(''); setError(''); onClose(); };
  async function create() {
    if (busy) return;
    setError('');
    if (!label.trim() || fields.some(field => !values[field.key])) { setError('Заполните название и все поля секрета.'); return; }
    let value;
    if (fields[0].key === 'json') {
      try {
        const parsed = JSON.parse(values.json);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error();
        value = JSON.stringify(parsed);
      } catch { setError('Введите JSON-объект по документации провайдера.'); return; }
    } else if (fields[0].key === 'value') value = values.value;
    else value = JSON.stringify(Object.fromEntries(fields.map(field => [field.key, values[field.key]])));
    if (purpose === 'processing' && provider === 'savagepay' &&
        (value.length > 4096 || /\s/.test(value) || [...value].some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127) || /^[{["]/.test(value))) {
      setError('Вставьте только API-токен SavagePay: без Bearer, JSON, кавычек и пробелов.'); return;
    }
    if (purpose === 'receipt_storage' && !/^[a-fA-F0-9]{64}$/.test(value)) { setError('Ключ шифрования должен содержать ровно 64 шестнадцатеричных символа.'); return; }
    if (purpose === 'receipt_link' && value.length < 32) { setError('Ключ ссылок должен содержать не менее 32 символов.'); return; }
    setBusy(true);
    setValues({});
    try {
      const result = await v4API.createSecret({ label: label.trim(), purpose, value });
      value = undefined;
      if (typeof result?.ref !== 'string' || !result.ref) throw new Error();
      setLabel('');
      onCreated(secretMetadata(result));
    } catch {
      setError('Не удалось подтвердить сохранение секрета. Обновите список перед повтором. Значение очищено; при необходимости введите его заново.');
    } finally { value = undefined; setBusy(false); }
  }
  return <Dialog open fullWidth maxWidth="sm" onClose={busy ? undefined : close} aria-labelledby="secret-editor-title">
    <DialogTitle id="secret-editor-title">{title}</DialogTitle>
    <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
      <Alert severity="info">Секрет сохраняется отдельно в зашифрованном виде. Прочитать или удалить его из интерфейса нельзя. После создания сохраните конфигурацию; если её сохранение не удалось, секрет останется доступен в списке.</Alert>
      {purpose === 'receipt_storage' && <Alert severity="warning">Введите только прежний ключ существующих документов. Новый ключ сделает старые файлы нечитаемыми. Не генерируйте замену.</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      <TextField label="Название секрета" value={label} disabled={busy} onChange={event => setLabel(event.target.value)} inputProps={{ maxLength: 120 }} autoComplete="off" />
      {fields.map(field => <TextField key={field.key} label={field.label} type="password" autoComplete="new-password" value={values[field.key] || ''} disabled={busy} onChange={event => setValues(previous => ({ ...previous, [field.key]: event.target.value }))} />)}
      {purpose === 'processing' && provider === 'savagepay' && <Typography variant="body2">Вставьте API-токен из кабинета SavagePay обычным текстом, без Bearer, JSON и кавычек. Секрет подписи callback сохраните отдельно как секрет вебхука.</Typography>}
      {purpose === 'processing' && provider === '911pay' && <Typography variant="body2">Access Token берётся в разделе «Интеграция» кабинета 911pay. ID мерчанта — UUID в 911pay, не ID магазина Paylov. Токен Telegram-бота для видео задаётся отдельной сохранённой ссылкой в параметрах маршрута.</Typography>}
      {fields[0].key === 'json' && <Typography variant="body2">Формат: JSON-объект с полями из документации выбранного провайдера. Значения не помещаются в URL или параметры маршрута.</Typography>}
    </Stack></DialogContent>
    <DialogActions><Button disabled={busy} onClick={close}>Отмена</Button><Button disabled={busy} variant="contained" onClick={create}>{busy ? 'Сохранение секрета…' : 'Создать секрет'}</Button></DialogActions>
  </Dialog>;
}
